// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { Dictionary } from "@insite/client-framework/Common/Types";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import setFlyoutDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetFlyoutDrawerIsOpen";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrencies,
    getFulfillmentLabel,
    getIsPunchOutSession,
    getLanguages,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setCurrency from "@insite/client-framework/Store/Context/Handlers/SetCurrency";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import removeCart from "@insite/client-framework/Store/Data/Carts/Handlers/RemoveCart";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getCurrentPage, getHeader, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getWidgetsByPageId } from "@insite/client-framework/Store/Data/Widgets/WidgetSelectors";
import { getPageLinkByPageType, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps, IconProps, IconWrapper } from "@insite/mobius/Icon";
import Globe from "@insite/mobius/Icons/Globe";
import MapPin from "@insite/mobius/Icons/MapPin";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { MappedLink } from "@insite/mobius/Menu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import omitSingle from "@insite/mobius/utilities/omitSingle";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CustomAppState } from "@tps/App/Store/Reducers";
import { xIsAuthenticated } from "@tps/App/Store/Selectors/GeneralSelectors";
import PanelMenu, { PanelMenuPresentationProps, RowWrapper } from "@tps/MobiusOverrides/PanelMenu";
import { HeaderSize } from "@tps/Overrides/Pages/Header";
import { getMappedLink, getParameter } from "@tps/Overrides/Widgets/Header/HeaderLinkList";
import IconHeaderMenuHover from "@tps/XDL/Icons/IconHeaderMenuHover";
import IconHeaderMenuInactive from "@tps/XDL/Icons/IconHeaderMenuInactive";
import IconTextLinkPencilEditInactive from "@tps/XDL/Icons/IconTextLinkPencilEditInactive";
import { buttonTransparent } from "@tps/XDL/PreStyle/button";
import { color, hideElementOnPrint, xGetFulfillmentLabel, xScreen } from "@tps/XDL/Utilities";
import isEqual from "lodash/isEqual";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css, ThemeProps, withTheme } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const links =
        getWidgetsByPageId(state, getHeader(state).id).find((widget: WidgetProps) => {
            return widget.type === "Header/HeaderLinkList";
        })?.fields.links ?? [];

    const mappedLinks = [];
    for (const link of links) {
        const {
            fields: { linkType, destination, openInNewWindow, overriddenTitle, maxDepth, title },
        } = link;

        const linkMapped = getMappedLink(
            state,
            maxDepth,
            overriddenTitle,
            linkType,
            destination,
            openInNewWindow,
            title,
        );
        linkMapped && mappedLinks.push(linkMapped);
    }
    const categoryIdsToLoad: Dictionary<number> = {};

    const pageType = getCurrentPage(state).type;

    return {
        currencies: getCurrencies(state),
        currentCurrencyId: state.context.session.currency?.id,
        currentCurrencySymbol: state.context.session.currency?.currencySymbol,
        languages: getLanguages(state),
        currentLanguage: state.context.session.language,
        currentLocation: getLocation(state),
        userName: state.context.session?.userName,
        isGuest: state.context.session?.isGuest,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage"),
        vmiDashboardPageUrl: getPageLinkByPageType(state, "VmiDashboardPage")?.url,
        homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
        showCustomerMenuItem: getSettingsCollection(state).accountSettings.enableWarehousePickup,
        // XNG-Change use xGetFulfillmentLabel
        fulfillmentLabel: xGetFulfillmentLabel(state),
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        flyoutDrawerIsOpen: state.components.addressDrawer.flyoutDrawerIsOpen,
        isPunchOutSession: getIsPunchOutSession(state),
        cart: getCurrentCartState(state).value,
        // Xng-Changes
        isAuthenticated: xIsAuthenticated(state),
        notificationBarFromState: state.xHeader.notificationBar,
        isCheckoutPages:
            pageType === "CheckoutShippingPage" ||
            pageType === "CheckoutReviewAndSubmitPage" ||
            pageType === "OrderConfirmationPage" ||
            pageType === "RfqRequestQuotePage" ||
            pageType === "RfqConfirmationPage",
        cartPageUrl: getPageLinkByPageType(state, "CartPage"),

        categoryIdsToLoad,
        categoryErrorStatusCodeById: state.data.categories.errorStatusCodeById,
        categoryIdsCalled: state.data.categories.categoryIdsCalled,
        headerLinkListLinkFields: mappedLinks,
        isPickUp: getSession(state).fulfillmentMethod === FulfillmentMethod.PickUp,
        changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage"),
    };
};

const mapDispatchToProps = {
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
    // XNG-Change
    loadCategories,
};

interface OwnProps {
    links: MappedLink[];
    showQuickOrder: boolean;
    quickOrderLink: PageLinkModel | undefined;
    displayVmiNavigation?: boolean;
    vmiPageLinks?: (PageLinkModel | undefined)[];
    displayModeSwitch?: boolean;
    currentMode?: string;
    isCompactHeaderFlyout?: boolean;
    showFlyoutPricingToggle?: boolean;
    showFlyoutInventoryToggle?: boolean;
    rootElementSelector?: string;
    allowOpenParentLinks?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

export interface NavigationDrawerStyles {
    menuTriggerButton?: ButtonPresentationProps;
    drawer?: DrawerPresentationProps;
    drawerSectionWrapper?: InjectableCss;
    currencySelectorWrapper?: InjectableCss;
    languageSelectorWrapper?: InjectableCss;
    panelMenu?: PanelMenuPresentationProps;
    panelSectionWrapper?: InjectableCss;
    menuRowIcon?: IconProps;
    userRowTypography?: TypographyPresentationProps;
    punchOutUserRowText?: TypographyPresentationProps;
    mainNavigationRow?: PanelRowPresentationProps;
    mainNavigationRowIcon?: IconProps;
    punchOutNavigationRowIcon?: IconProps;
    mainNavigationRowTypography?: TypographyPresentationProps;
    logoLinks?: PanelRowPresentationProps & { typographyProps: TypographyPresentationProps };
    currencySymbol?: TypographyPresentationProps;
    changeCustomerRow?: PanelRowPresentationProps;
    changeCustomerRowContainer?: GridContainerProps;
    fulfillmentMethodGridItem?: GridItemProps;
    addressesGridItem?: GridItemProps;
    pickUpAddressGridItem?: GridItemProps;
    applyButtonGridItem?: GridItemProps;
    signOutRow?: PanelRowPresentationProps;
    signOutRowText?: TypographyPresentationProps;
    // XNG-Changes
    mainNavigationExpandedRow?: PanelRowPresentationProps;
    separator?: InjectableCss;
    icon?: IconPresentationProps;
    fulfillmentSectionWrapper?: InjectableCss;
    fulfillmentMethodValue?: TypographyPresentationProps;
    mainNavigationRowLink?: LinkPresentationProps;
}

const StyledSection = getStyledWrapper("section");
const StyledSpan = getStyledWrapper("span");

export const navigationDrawerStyles: NavigationDrawerStyles = {};

const styles = navigationDrawerStyles;

const NavigationDrawer = ({
    links,
    showQuickOrder,
    quickOrderLink,
    displayVmiNavigation,
    vmiPageLinks,
    displayModeSwitch,
    currentMode,
    isCompactHeaderFlyout,
    showFlyoutPricingToggle,
    showFlyoutInventoryToggle,
    rootElementSelector,
    currencies,
    currentCurrencyId,
    currentCurrencySymbol,
    languages,
    currentLanguage,
    currentLocation,
    userName,
    isGuest,
    myAccountPageLink,
    signInUrl,
    vmiDashboardPageUrl,
    homePageUrl,
    headerLinkListLinkFields,
    showCustomerMenuItem,
    fulfillmentLabel,
    navDrawerIsOpen,
    flyoutDrawerIsOpen,
    isPunchOutSession,
    cart,
    history,
    theme,
    setCurrency,
    setLanguage,
    signOut,
    cancelPunchOut,
    setInitialValues,
    setNavDrawerIsOpen,
    setFlyoutDrawerIsOpen,
    removeCart,
    setSignInOverlayIsOpen,
    allowOpenParentLinks,
    // XNG-Changes
    isAuthenticated,
    notificationBarFromState,
    isCheckoutPages,
    cartPageUrl,
    categoryIdsToLoad,
    categoryErrorStatusCodeById,
    categoryIdsCalled,
    isPickUp,
    changeCustomerUrl,
}: Props) => {
    const prevPropsRef = React.useRef(categoryIdsToLoad);
    const [notificationBar, setNotificationBar] = useState(true);

    React.useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        const closedState = sessionStorage?.getItem("closed");
        setNotificationBar(!(closedState && closedState === "true"));
    }, [notificationBarFromState]);

    React.useEffect(() => {
        loadCategoriesIfNeeded();
    }, []);

    React.useEffect(() => {
        if (prevPropsRef.current) {
            if (!isEqual(categoryIdsToLoad, prevPropsRef.current)) {
                loadCategoriesIfNeeded(true);
            }
        }
        prevPropsRef.current = categoryIdsToLoad;
    });

    const loadCategoriesIfNeeded = (isComponentDidUpdate = false) => {
        if (categoryIdsToLoad) {
            for (const categoryId of Object.keys(categoryIdsToLoad)) {
                if (!categoryErrorStatusCodeById?.[categoryId]) {
                    const maxDepth = categoryIdsToLoad[categoryId];
                    if (!categoryIdsCalled?.includes(categoryId) || isComponentDidUpdate) {
                        loadCategories(getParameter(categoryId, maxDepth));
                    }
                }
            }
        }
    };
    const openDrawer = () => {
        isCompactHeaderFlyout ? setFlyoutDrawerIsOpen({ isOpen: true }) : setNavDrawerIsOpen({ navDrawerIsOpen: true });
        setInitialValues({});
    };

    const closeDrawer = () => {
        if (isCompactHeaderFlyout) {
            setFlyoutDrawerIsOpen({ isOpen: false });
            setTimeout(() => setFlyoutDrawerIsOpen({ isOpen: undefined }), 300);
            return;
        }

        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        setTimeout(() => setNavDrawerIsOpen({ navDrawerIsOpen: undefined }), 300);
    };

    const signInPanelClickHandler = (event: React.MouseEvent) => {
        if (theme.signIn?.useOverlay) {
            event.preventDefault();
            setSignInOverlayIsOpen({ isOpen: true });
        }
        closeDrawer();
    };
    const signInPanelKeyPressHandler = (event: React.KeyboardEvent) => {
        if (event.key === " " && history && signInUrl?.url) {
            event.preventDefault();
            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
            } else {
                history.push(signInUrl.url);
            }
            closeDrawer();
        }
    };

    const onSignOutHandler = () => {
        setNavDrawerIsOpen({ navDrawerIsOpen: false });
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    const navigationModeChangeHandler = (navigationMode: "Storefront" | "Vmi") => {
        setCookie(NavigationModeCookieName, navigationMode);
        if (navigationMode === "Vmi") {
            vmiDashboardPageUrl && history.push(vmiDashboardPageUrl);
        } else if (cart?.status === "AlternateCart") {
            removeCart({
                cartId: API_URL_CURRENT_FRAGMENT,
                isAlternateCart: true,
                onSuccess: () => {
                    homePageUrl && history.push(homePageUrl);
                },
            });
        } else {
            homePageUrl && history.push(homePageUrl);
        }
    };

    const currentPageUrl = currentLocation.pathname;
    const isOpen = isCompactHeaderFlyout ? flyoutDrawerIsOpen : navDrawerIsOpen;
    let drawerTopValue = isCheckoutPages ? HeaderSize.checkoutMobileHeader : HeaderSize.mobile;
    drawerTopValue = !notificationBar ? drawerTopValue - 33 : drawerTopValue;
    return (
        <>
            {/* XNG-CHANGES : remove menu trigger button on mobile */}
            <Drawer
                draggable={false}
                className="drawer"
                position="left"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={closeDrawer}
                contentLabel="menu drawer"
                data-test-selector="drawerSection"
                rootElementSelector={rootElementSelector}
                cssOverrides={{
                    scrim: css`
                        top: ${drawerTopValue}px;
                        background-color: ${color("common.ui7", 0.5)};
                        ${hideElementOnPrint}
                    `,
                    drawerTitle: css`
                        display: none;
                    `,
                    drawerContent: css`
                        overflow-x: hidden;
                    `,
                    drawerBody: css`
                        border-top: 1px solid ${color("common.border")};
                        ${xScreen("lte", "md")} {
                            width: 360px;
                        }
                        ${xScreen("lte", "xs")} {
                            width: 100%;
                        }
                        top: ${drawerTopValue}px;
                        background: ${color("common.white")};
                        box-shadow: none;
                    `,
                }}
            >
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {/* covers MainNavigation functionality */}
                    {!displayVmiNavigation &&
                        links.map((link, index) => {
                            let menu;
                            let panelRow;
                            const renderChildren =
                                link.children &&
                                link.children.length > 0 &&
                                link.children?.some(o => !o.excludeFromNavigation);
                            let menuItem: JSX.Element | null = (
                                <PanelRow
                                    href={link.url}
                                    target={link.openInNewWindow ? "_blank" : ""}
                                    className="main-navigation-row"
                                    onClick={closeDrawer}
                                    {...styles.mainNavigationRow}
                                >
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {link.title}
                                    </Typography>
                                </PanelRow>
                            );

                            if (renderChildren) {
                                if (link.url) {
                                    panelRow = <PanelRow hasChildren></PanelRow>;
                                } else {
                                    panelRow = React.cloneElement(menuItem, { hasChildren: true });
                                    menuItem = null;
                                }
                                menu = (
                                    <PanelMenu
                                        className="panel-menu"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        currentUrl={currentPageUrl}
                                        closeOverlay={closeDrawer}
                                        panelTrigger={panelRow}
                                        menuItems={link.children}
                                        maxDepth={link.maxDepth || 3}
                                        layer={0}
                                        {...styles.panelMenu}
                                    />
                                );
                            }
                            return (
                                <RowWrapper
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    css={css`
                                        padding: 0;
                                    `}
                                >
                                    {menuItem}
                                    {menu}
                                </RowWrapper>
                            );
                        })}
                    {!displayVmiNavigation && showQuickOrder && quickOrderLink && (
                        <PanelRow
                            className="main-navigation-row"
                            isCurrent={currentPageUrl === quickOrderLink.url}
                            onClick={closeDrawer}
                            href={quickOrderLink.url}
                            {...styles.mainNavigationRow}
                        >
                            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                                {quickOrderLink.title}
                            </Typography>
                        </PanelRow>
                    )}
                    {displayVmiNavigation &&
                        vmiPageLinks &&
                        vmiPageLinks.map(
                            link =>
                                link && (
                                    <PanelRow
                                        className="main-navigation-row"
                                        key={link.id}
                                        isCurrent={currentPageUrl === link.url}
                                        onClick={closeDrawer}
                                        href={link.url}
                                        {...styles.mainNavigationRow}
                                    >
                                        <Typography
                                            className="main-navigation-row-text"
                                            {...styles.mainNavigationRowTypography}
                                        >
                                            {link.title}
                                        </Typography>
                                    </PanelRow>
                                ),
                        )}
                    {/* My Account/SignIn and Cart */}
                    {isAuthenticated && !isPunchOutSession ? (
                        <>
                            <PanelMenu
                                className="panel-menu"
                                currentUrl={currentPageUrl}
                                panelTrigger={
                                    <PanelRow className="main-navigation-row" hasChildren {...styles.mainNavigationRow}>
                                        <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                            <Link
                                                onClick={closeDrawer}
                                                href={myAccountPageLink?.url}
                                                className="main-navigation-row-text"
                                                {...styles.mainNavigationRowLink}
                                            >
                                                {translate("My Account")}
                                            </Link>
                                        </StyledSpan>
                                    </PanelRow>
                                }
                                menuItems={myAccountPageLink?.children ? myAccountPageLink?.children : []}
                                maxDepth={3}
                                closeOverlay={closeDrawer}
                                layer={0}
                                {...styles.panelMenu}
                            />
                        </>
                    ) : isPunchOutSession ? (
                        <PanelRow className="main-navigation-row" {...styles.mainNavigationRow}>
                            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                <Typography
                                    className="main-navigation-row-text"
                                    {...styles.mainNavigationRowTypography}
                                >
                                    {userName}
                                </Typography>
                            </StyledSpan>
                        </PanelRow>
                    ) : (
                        <>
                            <PanelRow
                                className="main-navigation-row"
                                {...styles.mainNavigationRow}
                                isCurrent={currentPageUrl === signInUrl?.url}
                                onClick={closeDrawer}
                                onKeyPress={(event: React.KeyboardEvent) => {
                                    if (event.key === " " && history && signInUrl?.url) {
                                        event.preventDefault();
                                        closeDrawer();
                                        history.push(signInUrl.url);
                                    }
                                }}
                                href={signInUrl?.url}
                            >
                                <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {translate("Sign In")}
                                    </Typography>
                                </StyledSpan>
                            </PanelRow>
                        </>
                    )}

                    <PanelRow
                        className="main-navigation-row"
                        {...styles.mainNavigationRow}
                        onClick={closeDrawer}
                        href={cartPageUrl?.url}
                    >
                        <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                            <Typography className="main-navigation-row-text" {...styles.mainNavigationRowTypography}>
                                {translate("Cart")}
                            </Typography>
                        </StyledSpan>
                    </PanelRow>
                </StyledSection>
                <StyledWrapper {...styles.separator}></StyledWrapper>

                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    {headerLinkListLinkFields &&
                        headerLinkListLinkFields.length > 0 &&
                        headerLinkListLinkFields.map((link, index) => {
                            if (!link.url || !link.title) {
                                return null;
                            }
                            if (link?.children && link?.children.length > 0 && link.childrenType === "CascadingMenu") {
                                return (
                                    <PanelMenu
                                        className="panel-menu"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        currentUrl={currentPageUrl}
                                        closeOverlay={closeDrawer}
                                        panelTrigger={
                                            <PanelRow
                                                className="main-navigation-row"
                                                isCurrent={link.url === currentPageUrl}
                                                hasChildren
                                                allowOpenParentLinks={allowOpenParentLinks}
                                                {...styles.mainNavigationRow}
                                            >
                                                    <Link
                                                        className="main-navigation-row-text"
                                                        {...styles.mainNavigationRowLink}
                                                        href={link.url}
                                                        onClick={closeDrawer}
                                                        target={
                                                            headerLinkListLinkFields[index]?.openInNewWindow
                                                                ? "_blank"
                                                                : ""
                                                        }
                                                    >
                                                        {link.title}
                                                    </Link>
                                            </PanelRow>
                                        }
                                        menuItems={link.children}
                                        maxDepth={link.maxDepth || 3}
                                        layer={0}
                                        // allowOpenParentLinks={allowOpenParentLinks}
                                        {...styles.panelMenu}
                                    />
                                );
                            }
                            return (
                                <PanelRow
                                    key={link.title + String(index)}
                                    {...(styles.logoLinks && omitSingle(styles.logoLinks, "typographyProps"))}
                                    isCurrent={currentPageUrl === link.url}
                                    onClick={closeDrawer}
                                    onKeyPress={(event: React.KeyboardEvent) => {
                                        if (event.key === " " && history && link?.url) {
                                            event.preventDefault();
                                            closeDrawer();
                                            history.push(link.url);
                                        }
                                    }}
                                    href={link.url}
                                    target={headerLinkListLinkFields[index]?.openInNewWindow ? "_blank" : ""}
                                >
                                    <Typography {...styles.logoLinks?.typographyProps}>{link.title}</Typography>
                                </PanelRow>
                            );
                        })}
                </StyledSection>
                <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                    <SelectorMenu
                        options={currencies?.map(c => {
                            return {
                                title: c.currencyCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setCurrency({ currencyId: c.id });
                                        closeDrawer();
                                    },
                                },
                            };
                        })}
                        closeModal={closeDrawer}
                        currentOption={currencies?.find(c => c.id === currentCurrencyId)?.currencyCode}
                        currentOptionIcon={<Typography {...styles.currencySymbol}>{currentCurrencySymbol}</Typography>}
                    />
                    <SelectorMenu
                        dataTestSelector="mobileLanguageSelector"
                        options={languages?.map(l => {
                            return {
                                title: l.languageCode.toUpperCase(),
                                clickableProps: {
                                    onClick: () => {
                                        setLanguage({ languageId: l.id });
                                        closeDrawer();
                                    },
                                },
                            };
                        })}
                        currentOption={currentLanguage?.languageCode}
                        closeModal={closeDrawer}
                        currentOptionIcon={
                            currentLanguage?.imageFilePath ? (
                                <LogoImage className="language-image" src={currentLanguage.imageFilePath} alt="" />
                            ) : (
                                <Icon className="menu-row-icon" src={Globe} {...styles.menuRowIcon} />
                            )
                        }
                    />
                    {displayModeSwitch && (
                        <SelectorMenu
                            options={[
                                {
                                    title: translate("Storefront"),
                                    clickableProps: {
                                        onClick: () => {
                                            navigationModeChangeHandler("Storefront");
                                            closeDrawer();
                                        },
                                    },
                                },
                                {
                                    title: translate("Vendor Managed Inventory"),
                                    clickableProps: {
                                        onClick: () => {
                                            navigationModeChangeHandler("Vmi");
                                            closeDrawer();
                                        },
                                    },
                                },
                            ]}
                            closeModal={closeDrawer}
                            currentOption={
                                currentMode === "Vmi" ? translate("Vendor Managed Inventory") : translate("Storefront")
                            }
                        />
                    )}
                </StyledSection>

                {isAuthenticated && (
                    <>
                        <StyledWrapper {...styles.separator}></StyledWrapper>
                        <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                            <PanelRow
                                className="main-navigation-row"
                                {...styles.mainNavigationRow}
                                onClick={closeDrawer}
                                onKeyPress={(event: React.KeyboardEvent) => {
                                    if (event.key === " " && history && changeCustomerUrl?.url) {
                                        event.preventDefault();
                                        history.push(changeCustomerUrl?.url);
                                        closeDrawer();
                                    }
                                }}
                                href={changeCustomerUrl?.url}
                            >
                                <StyledSpan className="panel-section-wrapper" {...styles.fulfillmentSectionWrapper}>
                                    <Typography {...styles.mainNavigationRowTypography}>
                                        {translate("Fulfillment Method/Address")}:
                                    </Typography>
                                    <Typography {...styles.fulfillmentMethodValue}>
                                        {!fulfillmentLabel
                                            ? isPickUp
                                                ? translate("Select Branch")
                                                : translate("Select Shipping Address")
                                            : fulfillmentLabel}
                                        <Icon {...styles.icon} src={IconTextLinkPencilEditInactive} />
                                    </Typography>
                                </StyledSpan>
                            </PanelRow>
                        </StyledSection>

                        <StyledWrapper {...styles.separator}></StyledWrapper>

                        <StyledSection className="drawer-section-wrapper" {...styles.drawerSectionWrapper}>
                            <PanelRow className="sign-out-row" onClick={onSignOutHandler} {...styles.signOutRow}>
                                <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                                    <Typography
                                        className="main-navigation-row-text"
                                        {...styles.mainNavigationRowTypography}
                                    >
                                        {isPunchOutSession ? translate("Cancel PunchOut") : translate("Log Out")}
                                    </Typography>
                                </StyledSpan>
                            </PanelRow>
                        </StyledSection>
                    </>
                )}
            </Drawer>
        </>
    );
};

const LogoImage = styled.img`
    margin-right: 10px;
    height: 22px;
`;

interface SelectorMenuProps {
    options?: MappedLink[];
    closeModal?: () => void;
    currentOption?: string;
    currentOptionIcon?: React.ReactNode;
    dataTestSelector?: string;
}

const SelectorMenu: React.FC<SelectorMenuProps> = ({
    options,
    closeModal,
    currentOption,
    currentOptionIcon,
    dataTestSelector,
}) => {
    if (!options || options.length <= 1) {
        return null;
    }

    const trigger = (
        <PanelRow hasChildren className="logo-links-row" {...styles.logoLinks}>
            <StyledSpan className="panel-section-wrapper" {...styles.panelSectionWrapper}>
                {currentOptionIcon}
                <Typography
                    className="logo-link-title"
                    data-test-selector={`${dataTestSelector}_currentOption`}
                    {...styles.logoLinks?.typographyProps}
                >
                    {currentOption}
                </Typography>
            </StyledSpan>
        </PanelRow>
    );

    return (
        <PanelMenu
            className="panel-menu"
            currentUrl={currentOption}
            data-test-selector={dataTestSelector}
            closeOverlay={closeModal}
            panelTrigger={trigger}
            menuItems={options}
            maxDepth={1}
            layer={0}
            {...styles.panelMenu}
        />
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(NavigationDrawer)));
